import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Loader2, Wand2 } from "lucide-react";

const VARIABLES = [
  "{{child_name}}", "{{child_age}}", "{{child_gender}}", "{{hobbies}}",
  "{{favorite_sport}}", "{{favorite_activities}}", "{{story_title}}",
  "{{story_content}}", "{{question_text}}", "{{activity_name}}",
  "{{reflection_text}}",
];

export default function AdminPrompts() {
  const [prompts, setPrompts] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/ai-prompts").then((r) => {
    setPrompts(r.data);
    if (!active && r.data.length) setActive(r.data[0]);
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await api.patch(`/admin/ai-prompts/${active.key}`, {
        template: active.template,
        model: active.model,
        temperature: parseFloat(active.temperature),
      });
      toast.success(`Saved v${(active.version || 1) + 1}`);
      load();
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-prompts-screen">
      <h1 className="font-display text-4xl mb-2">AI Prompts</h1>
      <p className="text-muted-foreground mb-8">Editable templates · Version-tracked · No deploy needed.</p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        <div className="card-soft p-6">
          <div className="flex flex-wrap gap-2 mb-5">
            {prompts.map((p) => (
              <button key={p.key} onClick={() => setActive(p)}
                       className={`chip ${active?.key === p.key ? "bg-[hsl(var(--neu-ink))] text-white border-transparent" : ""}`}
                       data-testid={`admin-prompt-tab-${p.key}`}>
                {p.title}
              </button>
            ))}
          </div>

          {active && (
            <>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Template</label>
              <textarea rows={14} value={active.template}
                         onChange={(e) => setActive({ ...active, template: e.target.value })}
                         className="field mt-2 font-mono text-sm"
                         data-testid="admin-prompt-template"/>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">Model</label>
                  <input value={active.model} onChange={(e) => setActive({ ...active, model: e.target.value })}
                          className="field mt-1" data-testid="admin-prompt-model"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">Temperature</label>
                  <input type="number" step="0.1" min="0" max="2" value={active.temperature}
                          onChange={(e) => setActive({ ...active, temperature: e.target.value })}
                          className="field mt-1" data-testid="admin-prompt-temp"/>
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between">
                <div className="text-xs text-muted-foreground">Current version: v{active.version}</div>
                <button onClick={save} disabled={busy} className="btn-primary"
                        data-testid="admin-prompt-save">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Wand2 className="w-4 h-4"/> Save new version</>}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="card-soft p-5 h-fit">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Variables</div>
          <div className="flex flex-wrap gap-1">
            {VARIABLES.map((v) => (
              <code key={v} className="text-xs px-2 py-1 rounded bg-muted">{v}</code>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            These tokens are replaced at runtime with the child's profile and the current week's content.
          </p>
        </div>
      </div>
    </div>
  );
}
